<script>
import Panel from '@/components/ui/Panel';
import Selectable from '@/components/selectable/Selectable';

export default {
  name: 'BlocksPanel',
  components: {
    Panel,
    Selectable
  },
  props: {
    blocks: Array
  },
  data() {
    return {
      bookmark: null,
      editor: {},
      selection: []
    };
  },
  methods: {
    show(options) {
      const editor = options.editor;
      this.bookmark = editor.getBookmark();
      this.editor = editor;
      this.$refs.panel.show();
    },

    hide() {
      this.$refs.panel.hide();
      this.code = '';
    },

    handleClick(block) {
      this.$refs.selectable.select(block);
    },

    handleDoubleClick(block) {
      this.$refs.selectable.select(block);
      this.handleSubmit();
    },

    handleHide() {
      this.editor.focus();
    },

    handleShow() {
      const firstBlock = this.$refs.blocks.querySelector('.block');
      const selectedBlock = this.$refs.blocks.querySelector('.is-selected');
      if (selectedBlock) {
        selectedBlock.focus();
      } else if (firstBlock) {
        firstBlock.focus();
      } else {
        this.editor.blur();
      }
    },

    handleSubmit() {
      const block = this.selection[0];
      this.$refs.panel.hide();
      this.editor.restoreBookmark(this.bookmark);
      if (block) {
        this.editor.insertContent(block.source);
      }
    }
  }
};
</script>

<template>
  <panel
    ref="panel"
    :title="$t('Blocks')"
    @show="handleShow()"
    @hide="handleHide()"
  >
    <el-form
      label-position="top"
      novalidate
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <selectable
          ref="selectable"
          v-model="selection"
          :items="blocks"
        >
          <div ref="blocks" class="blocks-container">
            <div v-if="blocks.length" class="blocks">
              <a
                v-for="block in blocks"
                :class="{
                  'blocks-item': true,
                  'is-selected': $refs.selectable && $refs.selectable.isSelected(block)
                }"
                :key="block.id"
                href="#"
                @click.prevent="handleClick(block)"
                @dblclick.prevent="handleDoubleClick(block)"
              >
                <h3 class="block-name">{{ block.name }}</h3>
                <small class="block-description">{{ block.description }}</small>
              </a>
            </div>
            <div v-else class="blocks blocks-empty">
              {{ $t('No Blocks') }}
            </div>
          </div>
        </selectable>
        <el-button type="primary" native-type="submit">
          {{ $t('Insert Block') }}
        </el-button>
      </fieldset>
    </el-form>
  </panel>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.blocks-container {
  background: $--color-white;
  border: solid 1px $--color-gray-20;
  border-radius: .25rem;
  line-height: 1.4;
  user-select: none;
  margin-bottom: 1.5rem;
}

.blocks {
  min-height: 21.25rem; // ~ 5 items
  max-height: calc(100vh - 200px);
  overflow: auto;
  padding: 0;

  &.blocks-empty {
    display: flex;
    color: $--color-gray-40;
    align-items: center;
    justify-content: center;
    color: $--color-gray-40;
  }

  @include breakpoint('sm-and-down') {
    max-height: calc(100vh - 150px);
  }
}

.blocks-item {
  display: block;
  padding: .75rem 1rem;
  transition: .1s background-color, .1s border-color, .1s color, .1s box-shadow;

  .block-name {
    font-size: 1rem;
    margin: 0 0 0 0;
  }

  .block-description {
    color: $--color-gray-40;
  }

  &.is-selected {
    background-color: $--color-primary-light-9;
    border-color: $--color-primary-light-7;

    .block-name,
    .block-description {
      color: $--color-primary;
    }
  }

  &:focus {
    outline: none;
    border-color: $--color-primary;
  }
}
</style>
